import { useEffect, useMemo, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';
import html2pdf from 'html2pdf.js';

/* ================= TYPES ================= */
interface InvoiceItem {
  productRef: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  vatRate: number;
  totalHT: number;
}

interface BonLivraison {
  id: number;
  numero: string;
}

interface CustomerInvoice {
  id: number;
  numero: string;
  date_facture: string;
  date_echeance: string;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  statut_paiement: 'IMPAYEE' | 'PAYEE_PARTIEL' | 'PAYEE' | 'ANNULEE' | 'EN_LITIGE';
  montant_paye: number;
  notes?: string;
  client: {
    raison_sociale: string;
    email: string;
  };
  bon_livraison: BonLivraison | null;
  lignes: InvoiceItem[];
}

/* ================= COMPONENT ================= */
export default function CustomerInvoicesList() {
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedInvoice, setSelectedInvoice] = useState<CustomerInvoice | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'paid' | 'overdue'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Virement');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceExterne, setReferenceExterne] = useState('');
  const [dateValeur, setDateValeur] = useState('');
  const [notes, setNotes] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  /* ================= FORMATAGE MONTANT ================= */
  const formatPrice = (amount: number | string) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount)) + ' GNF';
  };

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const res = await getResources(`factures-client`);
        setInvoices(Array.isArray(res.data.data) ? res.data.data : []);
      } catch {
        toast.error('Erreur lors du chargement des factures');
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  /* ================= FILTRES ================= */
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const isOverdue =
        inv.statut_paiement === 'IMPAYEE' &&
        new Date(inv.date_echeance) < new Date();

      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'sent' && inv.statut_paiement === 'IMPAYEE' && !isOverdue) ||
        (filterStatus === 'paid' && inv.statut_paiement === 'PAYEE') ||
        (filterStatus === 'overdue' && isOverdue);

      const matchesSearch =
        inv.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.client.raison_sociale.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesSearch;
    });
  }, [invoices, filterStatus, searchTerm]);

  /* ================= STATS ================= */
  const stats = useMemo(() => {
    const totalAmount = invoices.reduce((s, i) => s + Number(i?.total_ttc || 0), 0);
    const paidAmount = invoices.reduce((s, i) => s + Number(i?.montant_paye || 0), 0);

    return {
      total: invoices.length,
      sent: invoices.filter(i => i.statut_paiement === 'IMPAYEE').length,
      paid: invoices.filter(i => i.statut_paiement === 'PAYEE').length,
      overdue: invoices.filter(
        i => i.statut_paiement === 'IMPAYEE' && new Date(i.date_echeance) < new Date()
      ).length,
      totalAmount,
      paidAmount,
      remainingAmount: totalAmount - paidAmount,
    };
  }, [invoices]);

  /* ================= STATUT PAIEMENT - CORRESPONDANCE EXACTE AVEC LA BD ================= */
  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'IMPAYEE': return 'Impayée';
      case 'PAYEE_PARTIEL': return 'Payée partiellement';
      case 'PAYEE': return 'Payée';
      case 'ANNULEE': return 'Annulée';
      case 'EN_LITIGE': return 'En litige';
      default: return 'Inconnu';
    }
  };
/* ================= STATUT ICON ================= */
const getStatusIcon = (statut: string) => {
  switch (statut) {
    case 'PAYEE':
      return 'ri-checkbox-circle-fill text-green-600 text-2xl';
    case 'PAYEE_PARTIEL':
      return 'ri-progress-3-line text-orange-600 text-2xl';
    case 'IMPAYEE':
      return 'ri-time-line text-yellow-600 text-2xl';
    case 'ANNULEE':
      return 'ri-close-circle-fill text-red-600 text-2xl';
    case 'EN_LITIGE':
      return 'ri-scales-3-line text-purple-600 text-2xl';
    default:
      return 'ri-file-line text-gray-600 text-2xl';
  }
};
  const getStatusColor = (statut: string, dateEcheance: string) => {
    const isOverdue = new Date(dateEcheance) < new Date();

    switch (statut) {
      case 'PAYEE': return 'bg-green-100 text-green-800';
      case 'PAYEE_PARTIEL': return 'bg-blue-100 text-blue-800';
      case 'IMPAYEE': return isOverdue ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800';
      case 'ANNULEE': return 'bg-gray-100 text-gray-800';
      case 'EN_LITIGE': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  /* ================= PAIEMENT ================= */
  const handleRecordPayment = async () => {
    if (!selectedInvoice) return;

    const total = Number(selectedInvoice.total_ttc || 0);
    const paid = Number(selectedInvoice.montant_paye || 0);
    const amount = Number(paymentAmount);
    const remaining = total - paid;

    if (amount <= 0 || amount > remaining) {
      toast.error('Montant invalide');
      return;
    }

    if (!paymentDate) {
      toast.error('Date de paiement obligatoire');
      return;
    }

    if (paymentMethod !== 'ESPECES' && !referenceExterne) {
      toast.error('Référence externe obligatoire');
      return;
    }

    setPaymentLoading(true);

    try {
      await createResource(`factures-client/reglement`, {
        client_id: selectedInvoice.client.id,
        facture_client_id: selectedInvoice.id,
        montant: amount,
        mode: paymentMethod,
        date_paiement: paymentDate,
        date_valeur: dateValeur || null,
        reference_externe: paymentMethod !== 'ESPECES' ? referenceExterne : null,
        notes: notes || null,
      });

      toast.success('Règlement enregistré avec succès');

      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentMethod('Virement');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setReferenceExterne('');
      setDateValeur('');
      setNotes('');

      const res = await getResources(`factures-client`);
      setInvoices(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de l’enregistrement du règlement');
    } finally {
      setPaymentLoading(false);
    }
  };

  /* ================= IMPRESSION FACTURE ================= */
  const handlePrintFacture = (invoice: CustomerInvoice) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Impossible d'ouvrir la fenêtre d'impression");
      return;
    }

    const totalTTC = Number(invoice.total_ttc);
    const totalHT = invoice.total_ht ?? totalTTC / 1.1925;
    const tvaMontant = invoice.total_tva ?? totalTTC - totalHT;
    const montantPaye = Number(invoice.montant_paye || 0);
    const resteAPayer = totalTTC - montantPaye;

    // Récupération des lignes depuis le bon de livraison (comme dans ton API)
    const lignes = invoice.bon_livraison?.lignes || [];

    const blNumero = invoice.bon_livraison?.numero || '—';

    // URL du logo (à adapter avec ton vrai logo)
    const logoUrl = "https://via.placeholder.com/250x100/1e40af/ffffff?text=TON+LOGO"; // ← Remplace par ton logo réel

    const html = `<!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="utf-8">
      <title>Facture ${invoice.numero}</title>
      <style>
        body { 
          font-family: 'Segoe UI', Arial, sans-serif; 
          margin: 0; 
          background: #f9fafb; 
          color: #333; 
        }
        .container { 
          max-width: 900px; 
          margin: 40px auto; 
          background: white; 
          padding: 40px; 
          border-radius: 12px; 
          box-shadow: 0 10px 30px rgba(0,0,0,0.1); 
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 20px;
          border-bottom: 4px solid #2563eb;
          margin-bottom: 30px;
        }
        .logo {
          max-height: 90px;
          max-width: 280px;
          object-fit: contain;
        }
        .title-block {
          text-align: right;
        }
        .title-block h1 {
          color: #1e40af;
          margin: 0;
          font-size: 32px;
          font-weight: bold;
        }
        .title-block h2 {
          color: #1e3a8a;
          margin: 10px 0 0;
          font-size: 24px;
        }

        .info-grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 40px; 
          margin: 30px 0; 
        }
        .info-block h3 { 
          color: #1e40af; 
          border-bottom: 2px solid #dbeafe; 
          padding-bottom: 8px; 
          margin-bottom: 15px; 
          font-size: 18px;
        }
        .info-block p {
          margin: 8px 0;
          line-height: 1.6;
        }

        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 30px 0; 
          font-size: 15px;
        }
        th, td { 
          border: 1px solid #e5e7eb; 
          padding: 14px; 
          text-align: left; 
        }
        th { 
          background: #eff6ff; 
          color: #1e40af; 
          font-weight: 600;
        }
        .text-right { text-align: right; }
        .text-center { text-align: center; }

        .totals { 
          float: right; 
          width: 420px; 
          margin-top: 30px; 
          font-size: 16px;
        }
        .total-line { 
          display: flex; 
          justify-content: space-between; 
          padding: 10px 0; 
          border-top: 1px solid #e5e7eb; 
        }
        .total-final { 
          font-size: 20px; 
          font-weight: bold; 
          color: #1e40af; 
          border-top: 3px double #2563eb; 
          padding-top: 12px; 
        }

        .payment-info {
          margin-top: 50px;
          padding: 20px;
          background: ${montantPaye > 0 ? '#f0fdf4' : '#fefce8'};
          border-radius: 10px;
          border-left: 5px solid ${montantPaye > 0 ? '#16a34a' : '#f59e0b'};
          clear: both;
        }

        .notes { 
          margin-top: 50px; 
          padding: 20px; 
          background: #f0f9ff; 
          border-radius: 10px; 
          border-left: 5px solid #2563eb;
          clear: both;
        }

        /* ZONE DE SIGNATURE DOUBLE */
        .signature-section {
          margin-top: 100px;
          page-break-inside: avoid;
          clear: both;
        }
        .signature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 100px;
          margin-top: 40px;
        }
        .signature-block {
          text-align: center;
        }
        .signature-label {
          font-weight: bold;
          font-size: 17px;
          margin-bottom: 20px;
          color: #1e40af;
        }
        .signature-line {
          border-top: 2px dotted #444;
          height: 130px;
          margin: 20px 0;
          position: relative;
        }
        .signature-line::after {
          content: "Signature + Cachet";
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 14px;
          color: #555;
          background: white;
          padding: 0 20px;
          font-weight: bold;
        }
        .signature-info {
          margin-top: 40px;
          font-size: 14px;
          line-height: 1.8;
        }

        .footer { 
          margin-top: 120px; 
          text-align: center; 
          color: #6b7280; 
          font-size: 12px; 
          clear: both;
        }

        @media print {
          body { background: white; margin: 0; }
          .container { box-shadow: none; padding: 20px; border-radius: 0; }
        }
      </style>
    </head>
    <body>
      <div class="container">

        <!-- HEADER AVEC LOGO -->
        <div class="header">
          <img src="${logoUrl}" alt="Logo entreprise" class="logo">
          <div class="title-block">
            <h1>FACTURE</h1>
            <h2>N° ${invoice.numero}</h2>
          </div>
        </div>

        <!-- INFORMATIONS -->
        <div class="info-grid">
          <div class="info-block">
            <h3>Client</h3>
            <p><strong>Raison sociale :</strong> ${invoice.client.raison_sociale}</p>
            <p><strong>Email :</strong> ${invoice.client.email || 'Non renseigné'}</p>
          </div>

          <div class="info-block">
            <h3>Informations facture</h3>
            <p><strong>Date facture :</strong> ${new Date(invoice.date_facture).toLocaleDateString('fr-FR')}</p>
            <p><strong>Date échéance :</strong> ${new Date(invoice.date_echeance).toLocaleDateString('fr-FR')}</p>
            <p><strong>Bon de livraison :</strong> ${blNumero}</p>
          </div>
        </div>

        <!-- TABLEAU DES ARTICLES -->
        <table>
          <thead>
            <tr>
              <th>Référence</th>
              <th>Désignation</th>
              <th class="text-center">Qté</th>
              <th class="text-right">P.U. HT</th>
              <th class="text-right">Remise</th>
              <th class="text-right">TVA</th>
              <th class="text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            ${lignes.length > 0 ? lignes.map((l: any) => `
              <tr>
                <td>${l.produit?.reference || '—'}</td>
                <td>${l.produit?.designation || l.designation || 'Produit'}</td>
                <td class="text-center">${l.quantite}</td>
                <td class="text-right">${Number(l.prix_unitaire_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
                <td class="text-right">${l.remise_ligne ? `${l.remise_ligne}%` : '0%'}</td>
                <td class="text-right">19,25%</td>
                <td class="text-right">${Number(l.total_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</td>
              </tr>
            `).join('') : `
              <tr>
                <td colspan="7" class="text-center py-8 text-gray-500">
                  Aucune ligne disponible
                </td>
              </tr>
            `}
          </tbody>
        </table>

        <!-- TOTAUX -->
        <div class="totals">
          <div class="total-line"><span>Total HT :</span><span>${totalHT.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span></div>
          <div class="total-line"><span>TVA (19,25%) :</span><span>${tvaMontant.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span></div>
          <div class="total-line total-final"><span>Total TTC :</span><span>${totalTTC.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span></div>
          
          ${montantPaye > 0 ? `
            <div class="total-line" style="color: #16a34a; font-weight: bold;">
              <span>Montant payé :</span>
              <span>${montantPaye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
            </div>
            <div class="total-line" style="color: #ea580c; font-weight: bold;">
              <span>Reste à payer :</span>
              <span>${resteAPayer.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF</span>
            </div>
          ` : ''}
        </div>
        <div style="clear:both;"></div>

        <!-- PAIEMENT INFO -->
        ${montantPaye > 0 ? `
          <div class="payment-info">
            <strong>Statut de paiement :</strong> ${invoice.statut_paiement === 'PAYEE' ? 'Payée en totalité' : 'Payée partiellement'}<br>
            Montant réglé : ${montantPaye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF<br>
            Reste dû : ${resteAPayer.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} GNF
          </div>
        ` : ''}

        <!-- NOTES -->
        ${invoice.notes ? `
          <div class="notes">
            <strong>Notes :</strong><br>
            ${invoice.notes.replace(/\n/g, '<br>')}
          </div>
        ` : ''}

        <!-- SIGNATURE DOUBLE -->
        <div class="signature-section">
          <h3 style="text-align: center; color: #1e40af; margin-bottom: 40px; font-size: 22px;">
            ACCUSÉ DE RÉCEPTION DE LA FACTURE
          </h3>

          <div class="signature-grid">
            <div class="signature-block">
              <div class="signature-label">
                Émetteur (Vendeur)<br>
                <small>Cachet + Signature</small>
              </div>
              <div class="signature-line"></div>
              <div class="signature-info">
                <div>Date : _____________________</div>
                <div>Nom : _____________________</div>
                <div>Fonction : __________________</div>
              </div>
            </div>

            <div class="signature-block">
              <div class="signature-label">
                Client (Acheteur)<br>
                <small>Cachet + Signature</small>
              </div>
              <div class="signature-line"></div>
              <div class="signature-info">
                <div>Date : _____________________</div>
                <div>Nom : _____________________</div>
                <div>Fonction : __________________</div>
              </div>
            </div>
          </div>

          <p style="text-align: center; margin-top: 60px; font-style: italic; color: #555; font-size: 15px;">
            "J'accuse réception de la présente facture et en accepte les conditions de paiement."
          </p>
        </div>

        <div class="footer">
          Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>

      </div>
    </body>
    </html>`;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 1200);
  };


/* ================= ENVOYER PAR WHATSAPP ================= */
  const handleSendInvoiceWhatsApp = async (invoice: CustomerInvoice) => {
  try {
    const res = await createResource(`factures-client/${invoice.id}/send-whatsapp`, {});

    const { pdf_url, client_phone, suggested_message } = res.data;

    const message = encodeURIComponent(suggested_message);

    // Si numéro de téléphone disponible → WhatsApp direct avec le numéro
    if (client_phone) {
      const phone = client_phone.replace(/[^0-9+]/g, ''); // Nettoie le numéro
      window.open(`https://wa.me/${phone}?text=${message}%0A%0A${pdf_url}`, '_blank');
    } else {
      // Sinon, juste le message + lien
      window.open(`https://wa.me/?text=${message}%0A%0A${pdf_url}`, '_blank');
    }

    toast.success('WhatsApp ouvert avec la facture !');
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Erreur lors de la préparation WhatsApp');
  }
};

  const handleSendInvoiceByEmail = async (invoiceId: number) => {
    try {
      await createResource(`factures-client/${invoiceId}/send-mail`,{});
      toast.success('Facture envoyée par email');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de l’envoi');
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-screen-2xl mx-auto">
      {/* ================= RECHERCHE & FILTRES ================= */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="Rechercher par numéro ou client..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Toutes', value: 'all', count: stats.total },
              { label: 'Envoyées', value: 'sent', count: stats.sent },
              { label: 'Payées', value: 'paid', count: stats.paid },
              { label: 'En retard', value: 'overdue', count: stats.overdue },
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setFilterStatus(filter.value as any)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  filterStatus === filter.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ================= STATISTIQUES ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Factures" value={stats.total} icon="ri-file-text-line" color="blue" />
        <StatCard label="Montant Total" value={`${stats.totalAmount.toLocaleString('fr-FR')} GNF`} icon="ri-money-euro-circle-line" color="teal" />
        <StatCard label="Montant Payé" value={`${stats.paidAmount.toLocaleString('fr-FR')} GNF`} icon="ri-check-double-line" color="green" />
        <StatCard label="Reste à Payer" value={`${stats.remainingAmount.toLocaleString('fr-FR')} GNF`} icon="ri-time-line" color="orange" />
      </div>

      {/* ================= TABLEAU ================= */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Numéro</Th>
                <Th className="hidden sm:table-cell">Client</Th>
                <Th className="hidden md:table-cell">Date</Th>
                <Th className="hidden lg:table-cell">Échéance</Th>
                <Th>Montant TTC</Th>
                <Th>Reste</Th>
                <Th>Statut</Th>
                <Th className="text-center">Actions</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredInvoices.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">
                    Aucune facture trouvée
                  </td>
                </tr>
              ) : (
                filteredInvoices.map(inv => {
                  const total = Number(inv.total_ttc || 0);
                  const paid = Number(inv.montant_paye || 0);
                  const remaining = total - paid;

                  return (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <Td>
                        <div className="font-medium text-blue-600">{inv.numero}</div>
                        <div className="text-xs text-gray-500 sm:hidden">
                          {inv.client.raison_sociale}
                        </div>
                      </Td>
                      <Td className="hidden sm:table-cell">
                        <div>{inv.client.raison_sociale}</div>
                        <div className="text-xs text-gray-500">{inv.client.email}</div>
                      </Td>
                      <Td className="hidden md:table-cell">
                        {new Date(inv.date_facture).toLocaleDateString('fr-FR')}
                      </Td>
                      <Td className="hidden lg:table-cell">
                        {new Date(inv.date_echeance).toLocaleDateString('fr-FR')}
                      </Td>
                      <Td className="font-medium">{formatPrice(total)}</Td>
                      <Td>
                        <div className="font-medium text-orange-600">{formatPrice(remaining)}</div>
                        {paid > 0 && remaining > 0 && (
                          <div className="text-xs text-gray-500">
                            Payé : {formatPrice(paid)}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inv.statut_paiement, inv.date_echeance)}`}>
                          {getStatusLabel(inv.statut_paiement)}
                        </span>
                      </Td>
                      <Td>
                        <div className="flex items-center justify-center gap-3 flex-wrap">
                          <Icon
                            icon="ri-eye-line"
                            onClick={() => {
                              setSelectedInvoice(inv);
                              setShowDetailModal(true);
                            }}
                          />
                          {remaining > 0 && (
                            <Icon
                              icon="ri-money-euro-circle-line"
                              onClick={() => {
                                setSelectedInvoice(inv);
                                setPaymentAmount(remaining.toString());
                                setShowPaymentModal(true);
                              }}
                            />
                          )}
                          <Icon icon="ri-printer-line" onClick={() => handlePrintFacture(inv)} />
                          <Icon icon="ri-mail-send-line" onClick={() => handleSendInvoiceByEmail(inv.id)} />
                            <Icon
                              icon="ri-whatsapp-line"
                              onClick={() => handleSendInvoiceWhatsApp(inv)}
                              className="text-green-600 hover:text-green-800 text-2xl"
                            />
                        </div>
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODALES (inchangées, design conservé) ================= */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-5xl w-full max-h-[90vh] overflow-y-auto">

            {/* ================= EN-TÊTE ================= */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-10 h-10 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                    title="Retour"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      Facture {selectedInvoice.numero}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Client : <strong>{selectedInvoice.client.raison_sociale}</strong>
                    </p>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="flex items-center gap-3">
                  {(selectedInvoice.total_ttc - selectedInvoice.montant_paye) > 0 && (
                    <button
                      onClick={() => {
                        setShowPaymentModal(true);
                        setShowDetailModal(false);
                        setPaymentAmount((selectedInvoice.total_ttc - selectedInvoice.montant_paye).toString());
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition"
                    >
                      <i className="ri-money-euro-circle-line"></i>
                      Enregistrer un paiement
                    </button>
                  )}
                </div>
              </div>

              {/* ================= STATUT PRINCIPAL ================= */}
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(selectedInvoice.statut_paiement, selectedInvoice.date_echeance)}`}>
                  <i className={getStatusIcon(selectedInvoice.statut_paiement)}></i>
                  {getStatusLabel(selectedInvoice.statut_paiement)}
                </span>

                {/* Alerte retard */}
                {selectedInvoice.statut_paiement === 'IMPAYEE' && new Date(selectedInvoice.date_echeance) < new Date() && (
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-full text-sm font-bold">
                    <i className="ri-alarm-warning-fill"></i>
                    En retard
                  </span>
                )}
              </div>
            </div>

            {/* ================= INFORMATIONS GÉNÉRALES ================= */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Informations Générales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Date facture</label>
                  <p className="font-semibold">{new Date(selectedInvoice.date_facture).toLocaleDateString('fr-FR')}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Date échéance</label>
                  <p className={`font-semibold ${new Date(selectedInvoice.date_echeance) < new Date() && selectedInvoice.statut_paiement !== 'PAYEE' ? 'text-red-600' : ''}`}>
                    {new Date(selectedInvoice.date_echeance).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Total TTC</label>
                  <p className="text-xl font-bold text-blue-600">{formatPrice(selectedInvoice.total_ttc)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Reste à payer</label>
                  <p className={`text-xl font-bold ${(selectedInvoice.total_ttc - selectedInvoice.montant_paye) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatPrice(selectedInvoice.total_ttc - selectedInvoice.montant_paye)}
                  </p>
                </div>
              </div>
            </div>

            {/* ================= BON DE LIVRAISON ASSOCIÉ ================= */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-truck-line text-teal-600"></i>
                Bon de livraison associé
              </h3>
              {selectedInvoice.bon_livraison ? (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 inline-block">
                  <span className="font-medium text-blue-800">{selectedInvoice.bon_livraison.numero}</span>
                </div>
              ) : (
                <p className="text-gray-500 italic">Aucun bon de livraison lié</p>
              )}
            </div>

            {/* ================= ARTICLES ================= */}
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <i className="ri-list-check-2 text-teal-600"></i>
                Articles facturés
              </h3>
            {selectedInvoice.bon_livraison?.lignes && selectedInvoice.bon_livraison.lignes.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Réf.</th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">Désignation</th>
                          <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">Qté</th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">P.U. HT</th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Remise</th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">TVA</th>
                          <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">Total HT</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedInvoice.bon_livraison.lignes.map((ligne, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm">
                              {ligne.produit?.reference || ligne.produit_reference || '—'}
                            </td>
                            <td className="px-6 py-4 text-sm font-medium">
                              {ligne.produit?.designation || ligne.designation || 'Produit inconnu'}
                            </td>
                            <td className="px-6 py-4 text-center text-sm">{ligne.quantite}</td>
                            <td className="px-6 py-4 text-right text-sm">
                              {formatPrice(ligne.prix_unitaire_ht)}
                            </td>
                            <td className="px-6 py-4 text-right text-sm">
                              {ligne.remise_ligne ? `${ligne.remise_ligne}%` : '0%'}
                            </td>
                            <td className="px-6 py-4 text-right text-sm">19,25%</td>
                            <td className="px-6 py-4 text-right text-sm font-semibold">
                              {formatPrice(ligne.total_ht || ligne.quantite * ligne.prix_unitaire_ht)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <i className="ri-inbox-line text-5xl text-gray-300 mb-4"></i>
                    <p className="text-gray-500">Aucun article dans le bon de livraison</p>
                  </div>
                )}
            </div>

            {/* ================= TOTAUX ================= */}
            <div className="p-6 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end">
                <div className="w-full max-w-md space-y-4">
                  <div className="flex justify-between text-lg">
                    <span>Total HT</span>
                    <span className="font-semibold">{formatPrice(selectedInvoice.total_ht)}</span>
                  </div>
                  <div className="flex justify-between text-lg">
                    <span>TVA (19,25%)</span>
                    <span className="font-semibold">{formatPrice(selectedInvoice.total_tva)}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold text-blue-600 border-t-2 border-blue-200 pt-4">
                    <span>Total TTC</span>
                    <span>{formatPrice(selectedInvoice.total_ttc)}</span>
                  </div>

                  {selectedInvoice.montant_paye > 0 && (
                    <>
                      <div className="flex justify-between text-lg text-green-600 pt-4 border-t border-gray-300">
                        <span>Montant payé</span>
                        <span className="font-bold">{formatPrice(selectedInvoice.montant_paye)}</span>
                      </div>
                      <div className="flex justify-between text-xl font-bold text-orange-600">
                        <span>Reste à payer</span>
                        <span>{formatPrice(selectedInvoice.total_ttc - selectedInvoice.montant_paye)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ================= NOTES ================= */}
            {selectedInvoice.notes && (
              <div className="p-6 bg-blue-50 border-t border-blue-200">
                <h4 className="font-semibold text-lg text-blue-900 mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line"></i>
                  Notes
                </h4>
                <p className="text-gray-700 whitespace-pre-line">{selectedInvoice.notes}</p>
              </div>
            )}

            {/* ================= FOOTER ACTIONS ================= */}
            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
              <div></div> {/* Espace gauche vide pour équilibrer */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Fermer
                </button>

                <button
                  onClick={() => handlePrintFacture(selectedInvoice)}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium flex items-center gap-2"
                >
                  <i className="ri-printer-line"></i>
                  Imprimer
                </button>
              </div>
            </div>

          </div>
        </div>

      )}

      {/* ================= MODAL PAIEMENT ================= */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 
                          max-w-xl w-full max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <i className="ri-money-euro-circle-line text-3xl text-green-600"></i>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Enregistrer un paiement</h3>
                    <p className="text-gray-600 mt-1">Facture N° {selectedInvoice.numero}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="p-3 hover:bg-gray-100 rounded-lg transition"
                >
                  <i className="ri-close-line text-2xl text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Récap facture */}
              <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Client</p>
                    <p className="font-medium">{selectedInvoice.client.raison_sociale}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total TTC</p>
                    <p className="font-medium">{formatPrice(selectedInvoice.total_ttc)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Déjà payé</p>
                    <p className="font-medium text-green-700">{formatPrice(selectedInvoice.montant_paye)}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Reste à payer</p>
                    <p className="font-bold text-orange-600">{formatPrice(selectedInvoice.total_ttc - selectedInvoice.montant_paye)}</p>
                  </div>
                </div>
              </div>

              {/* Formulaire */}
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant *</label>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={e => setPaymentAmount(e.target.value)}
                    min="0.01"
                    max={selectedInvoice.total_ttc - selectedInvoice.montant_paye}
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mode de paiement *</label>
                  <select
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">selectionner...</option>
                    <option value="VIREMENT">Virement</option>
                    <option value="CHEQUE">Chèque</option>
                    <option value="ESPECES">Espèces</option>
                    <option value="MOBILE_MONEY">Mobile Money</option>
                    <option value="CARTE">Carte bancaire</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date paiement *</label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={e => setPaymentDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date valeur</label>
                    <input
                      type="date"
                      value={dateValeur}
                      onChange={e => setDateValeur(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                {paymentMethod !== 'ESPECES' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Référence externe *</label>
                    <input
                      type="text"
                      value={referenceExterne}
                      onChange={e => setReferenceExterne(e.target.value)}
                      placeholder="N° chèque, transaction ID..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Commentaire interne..."
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-4">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={paymentLoading}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {paymentLoading && <i className="ri-loader-4-line animate-spin"></i>}
                Enregistrer le paiement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= UI HELPERS ================= */
function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${className}`}>
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-3 text-sm text-gray-900 ${className}`}>{children}</td>;
}

function Icon({ icon, onClick }: { icon: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="p-2 rounded-lg hover:bg-gray-100 transition text-gray-600 hover:text-gray-900"
      title={icon.replace('ri-', '').replace('-line', '').replace(/-/g, ' ')}
    >
      <i className={`${icon} text-lg`} />
    </button>
  );
}

function StatCard({ label, value, icon, color = 'gray' }: { label: string; value: string | number; icon: string; color?: string }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    teal: 'bg-teal-100 text-teal-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
  }[color] || 'bg-gray-100 text-gray-600';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 ${colorClasses} rounded-lg flex items-center justify-center`}>
        <i className={`${icon} text-2xl`} />
      </div>
    </div>
  );
}